// src/components/teacher/TeacherDashboard.js
import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders, getStoredToken, handleUnauthorized } from "../../utils/api";
import { getPermissions } from "../../utils/permissions";
import { USE_DUMMY_DATA, DUMMY_TEACHER, DUMMY_COURSES } from "./dummyData";
import "./teacher.css";

import TeacherTimetable from "./TeacherTimetable";
import TeacherCourses from "./TeacherCourses";
import TeacherCourseDetail from "./TeacherCourseDetail";
import TeacherStudents from "./TeacherStudents";
import TeacherGrades from "./TeacherGrades";
import TeacherReports from "./TeacherReports";
import TeacherProfile from "./TeacherProfile";
import TeacherCampusInfo from "./TeacherCampusInfo";
import TeacherOverview from "./TeacherOverview";
import TeacherAttendance from "./TeacherAttendance";
import TeacherRemarks from "./TeacherRemarks";
import TeacherHomework from "./TeacherHomework";
import TeacherHomeworkDetail from "./TeacherHomeworkDetail";
import TeacherMaterials from "./TeacherMaterials";
import TeacherStudentDetail from "./TeacherStudentDetail";
import YearlyCalendarPage from "../shared/calendar/YearlyCalendarPage";

function TeacherDashboard({ onLogout }) {
  const [teacherData, setTeacherData] = useState(
    USE_DUMMY_DATA ? DUMMY_TEACHER : null,
  );
  const [courses, setCourses] = useState(USE_DUMMY_DATA ? DUMMY_COURSES : []);
  const [loading, setLoading] = useState(!USE_DUMMY_DATA);
  const permissions = getPermissions(localStorage.getItem("als_role"));

  useEffect(() => {
    if (USE_DUMMY_DATA) {
      localStorage.setItem("als_teacher_name", DUMMY_TEACHER.name);
      localStorage.setItem(
        "als_teacher_campus_id",
        String(DUMMY_TEACHER.campus_id),
      );
      localStorage.setItem("als_teacher_subject", DUMMY_TEACHER.subject);
      localStorage.setItem("als_teacher_email", DUMMY_TEACHER.email);
      localStorage.setItem(
        "als_teacher_employee_id",
        DUMMY_TEACHER.employee_id,
      );
      setLoading(false);
      return;
    }

    const fetchTeacherData = async () => {
      try {
        const token = getStoredToken();
        if (!token) {
          handleUnauthorized(onLogout);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/teachers/me`, {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
        });

        if (response.status === 401) {
          console.error("Teacher profile request unauthorized");
          handleUnauthorized(onLogout);
          return;
        }

        if (!response.ok) {
          throw new Error("Failed to fetch teacher data");
        }

        const data = await response.json();
        setTeacherData(data);
        localStorage.setItem("als_teacher_name", data.name || "");
        localStorage.setItem("als_teacher_campus_id", data.campus_id || "");
        localStorage.setItem("als_teacher_subject", data.subject || "");
        localStorage.setItem("als_teacher_email", data.email || "");
        localStorage.setItem("als_teacher_employee_id", data.employee_id || "");
      } catch (err) {
        console.error("Failed to fetch teacher data:", err);
      }
    };

    const fetchCourses = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/teachers/me/courses`, {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
        });

        if (response.status === 401) {
          handleUnauthorized(onLogout);
          return;
        }

        if (!response.ok) {
          throw new Error("Failed to fetch courses");
        }

        const data = await response.json();
        setCourses(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch courses:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherData();
    fetchCourses();
  }, [onLogout]);

  return (
    <div
      className="teacher-dashboard-content teacher-portal"
      data-teacher-ui="premium"
    >
      <Routes>
        <Route
          path="overview"
          element={
            <TeacherOverview
              teacherData={teacherData}
              courses={courses}
              loading={loading}
            />
          }
        />
        <Route
          path="timetable"
          element={
            <TeacherTimetable teacherData={teacherData} loading={loading} />
          }
        />
        <Route
          path="courses"
          element={<TeacherCourses courses={courses} loading={loading} />}
        />
        <Route
          path="courses/:assignmentId"
          element={<TeacherCourseDetail courses={courses} loading={loading} />}
        />
        <Route
          path="courses/:assignmentId/remarks"
          element={<TeacherRemarks courses={courses} loading={loading} />}
        />
        <Route
          path="courses/:assignmentId/homework"
          element={<TeacherHomework courses={courses} loading={loading} />}
        />
        <Route
          path="courses/:assignmentId/homework/:homeworkId"
          element={<TeacherHomeworkDetail courses={courses} loading={loading} />}
        />
        <Route
          path="courses/:assignmentId/students/:studentId"
          element={<TeacherStudentDetail loading={loading} />}
        />
        <Route
          path="courses/:assignmentId/materials"
          element={<TeacherMaterials courses={courses} loading={loading} />}
        />
        <Route
          path="students/:studentId"
          element={<TeacherStudentDetail loading={loading} />}
        />
        <Route
          path="attendance"
          element={
            <TeacherAttendance
              teacherData={teacherData}
              courses={courses}
              loading={loading}
            />
          }
        />
        <Route
          path="students"
          element={
            permissions.canViewAssignedStudents || USE_DUMMY_DATA ? (
              <TeacherStudents
                teacherData={teacherData}
                courses={courses}
                loading={loading}
              />
            ) : (
              <div className="tp-empty">
                <h3>Access Denied</h3>
                <p>You don't have permission to view student data.</p>
              </div>
            )
          }
        />
        <Route
          path="grades"
          element={
            permissions.canManageGrades || USE_DUMMY_DATA ? (
              <TeacherGrades
                teacherData={teacherData}
                courses={courses}
                loading={loading}
              />
            ) : (
              <div className="tp-empty">
                <h3>Access Denied</h3>
                <p>You don't have permission to manage grades.</p>
              </div>
            )
          }
        />
        <Route
          path="reports"
          element={
            permissions.canViewTeacherReports || USE_DUMMY_DATA ? (
              <TeacherReports teacherData={teacherData} />
            ) : (
              <div className="tp-empty">
                <h3>Access Denied</h3>
                <p>You don't have permission to view reports.</p>
              </div>
            )
          }
        />
        <Route
          path="calendar"
          element={<YearlyCalendarPage variant="teacher" />}
        />
        <Route
          path="remarks"
          element={<Navigate to="/teacher/courses" replace />}
        />
        <Route
          path="homework"
          element={<Navigate to="/teacher/courses" replace />}
        />
        <Route
          path="materials"
          element={<Navigate to="/teacher/courses" replace />}
        />
        <Route
          path="profile"
          element={
            <TeacherProfile teacherData={teacherData} onLogout={onLogout} />
          }
        />
        <Route
          path="campus"
          element={<TeacherCampusInfo teacherData={teacherData} />}
        />
        <Route path="*" element={<Navigate to="/teacher/overview" replace />} />
      </Routes>
    </div>
  );
}

export default TeacherDashboard;
